import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/**
 * Pedido de un dato con la piel del sistema, para reemplazar `window.prompt`.
 *
 * Mismo motivo que `useConfirm`: el diálogo nativo no toma el tema ni la
 * tipografía, y se puede silenciar.
 *
 *   const promptText = usePromptText();
 *   const url = await promptText({ title: 'Insertar enlace', label: 'URL' });
 *   if (url) { … }
 *
 * Devuelve `null` si el usuario cancela, para que el sitio de llamada distinga
 * "canceló" de "escribió vacío".
 */

export interface PromptOptions {
  title: ReactNode;
  description?: ReactNode;
  /** Etiqueta del campo. Default: "Valor". */
  label?: string;
  placeholder?: string;
  initialValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

type PromptText = (options: PromptOptions) => Promise<string | null>;

const PromptContext = createContext<PromptText | null>(null);

export function usePromptText(): PromptText {
  const ctx = useContext(PromptContext);
  if (!ctx) {
    throw new Error(
      'usePromptText() necesita <PromptProvider>, que se monta en AppLayout.',
    );
  }
  return ctx;
}

export function PromptProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<PromptOptions | null>(null);
  const [value, setValue] = useState('');
  const resolver = useRef<((value: string | null) => void) | null>(null);

  const promptText = useCallback<PromptText>((opts) => {
    setOptions(opts);
    setValue(opts.initialValue ?? '');
    return new Promise<string | null>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = useCallback((result: string | null) => {
    resolver.current?.(result);
    resolver.current = null;
    setOptions(null);
  }, []);

  const value_ = useMemo(() => promptText, [promptText]);

  return (
    <PromptContext.Provider value={value_}>
      {children}
      <Dialog
        open={options !== null}
        onOpenChange={(open) => {
          if (!open) settle(null);
        }}
      >
        {options && (
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{options.title}</DialogTitle>
              {options.description && (
                <DialogDescription>{options.description}</DialogDescription>
              )}
            </DialogHeader>
            <form
              className="grid gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                settle(value);
              }}
            >
              <Label htmlFor="prompt-field">{options.label ?? 'Valor'}</Label>
              <Input
                id="prompt-field"
                // El campo recibe el foco al abrir y se confirma con Enter.
                autoFocus
                value={value}
                placeholder={options.placeholder}
                onChange={(e) => setValue(e.target.value)}
              />
              <DialogFooter className="mt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => settle(null)}
                >
                  {options.cancelLabel ?? 'Cancelar'}
                </Button>
                <Button type="submit">{options.confirmLabel ?? 'Aceptar'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        )}
      </Dialog>
    </PromptContext.Provider>
  );
}
